import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Headphones, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';
import './News.css';

const defaultPressReleases = [
  {
    id: 8,
    title: 'Partsol Announces Next Generation AI Stem Cells, A New Foundation for Truth Based Cognitive Intelligence',
    date: 'January 28, 2026',
    excerpt: 'Revolutionary technology redefines how AI is designed, grown, and trusted, promising to transform high stakes industries from cancer research to national security.',
    image: '/press-release/Stem Cell News Release.jpg'
  },
  {
    id: 7,
    title: "National Security Leader Stu Shea Joins Partsol's Board of Strategic Advisors",
    date: 'November 12, 2025',
    excerpt: "Nationally recognized expert in defense intelligence and technology innovation joins Partsol's advisory board to strengthen AI leadership for national security.",
    image: '/press-release/release1.jpeg'
  },
  {
    id: 5,
    title: "Partsol Defense Secures U.S. Department of War Contract for Checkmate",
    date: 'November 6, 2025',
    excerpt: "Landmark $380,000 contract to deploy Atai Cognitive AI and Checkmate 4D Neural Network for safeguarding America's defense supply chains.",
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop'
  },
  {
    id: 6,
    title: 'Partsol Launches Partsol Futures: A New Era in AI Innovation',
    date: 'June 10, 2025',
    excerpt: 'Pioneering research and innovation lab focused on accelerating breakthrough AI applications for national security, smart infrastructure, and life sciences.',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop'
  },
  {
    id: 4,
    title: "World's First AI Stem Cell-Engineered Cognitive AI Platform Launched",
    date: 'March 31, 2025',
    excerpt: 'Partsol announces the launch of Atai, the world\'s first Cognitive AI platform engineered with breakthrough AI Stem Cell technology in Dublin, Ireland.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop'
  },
  {
    id: 3,
    title: "Partsol Named One of Tampa Bay's Top 25 Hottest Startups",
    date: 'January 7, 2025',
    excerpt: 'Tampa Business Journal recognizes Partsol as one of the most innovative companies driving transformation and economic growth in the region.',
    image: '/press-release/Tampa Skyline.jpg'
  },
  {
    id: 2,
    title: 'Partsol Secures Technology-Enabled Service Subscription with the US Army',
    date: 'November 26, 2024',
    excerpt: 'Significant agreement for technology-enabled service subscription powered by Cognitive AI and Absolute Truth algorithms for mission-critical operations.',
    image: '/press-release/release2.jpg'
  },
  {
    id: 1,
    title: 'Partsol Secures $17.5 Million in Capital to Expand Cognitive AI',
    date: 'November 13, 2024',
    excerpt: 'Funding round to support expansion of software and infrastructure and accelerate product development as Partsol redefines traditional due diligence.',
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop'
  }
];

// Helper function to parse date strings like "January 28, 2026"
const parseDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  return new Date(dateStr);
};

// Sort press releases by date (latest first)
const sortByDateDesc = (releases) => {
  return [...releases].sort((a, b) => parseDate(b.date) - parseDate(a.date));
};

const News = () => {
  const [pressReleases, setPressReleases] = useState(sortByDateDesc(defaultPressReleases));

  useEffect(() => {
    const fetchPressReleases = async () => {
      try {
        // Fetch all press releases and filter client-side for case-insensitive status
        const { data, error } = await supabase
          .from('press_releases')
          .select('*')
          .order('created_at', { ascending: false });

        console.log('Supabase press releases response:', { data, error });

        if (!error && data) {
          console.log('All press releases from DB:', data);
          // Filter for published status (case-insensitive)
          const publishedReleases = data.filter(item =>
            item.status?.toLowerCase() === 'published'
          );
          console.log('Published releases:', publishedReleases);

          if (publishedReleases.length > 0) {
            const formattedData = publishedReleases.map(item => ({
              id: item.id,
              title: item.title,
              date: item.date,
              excerpt: item.excerpt,
              image: item.image || item.image_url || 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop'
            }));
            setPressReleases(sortByDateDesc(formattedData));
            return;
          }
        }

        if (error) {
          console.log('Supabase error:', error);
        }

        // Try localStorage fallback - check admin_press_releases key
        const stored = localStorage.getItem('admin_press_releases');
        console.log('Trying localStorage fallback:', stored);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.length > 0) {
            const formattedData = parsed.filter(item =>
              item.status?.toLowerCase() === 'published'
            ).map(item => ({
              id: item.id,
              title: item.title,
              date: item.date,
              excerpt: item.excerpt,
              image: item.image || item.image_url || 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop'
            }));
            if (formattedData.length > 0) {
              setPressReleases(sortByDateDesc(formattedData));
            }
          }
        }
      } catch (err) {
        console.log('Error fetching press releases:', err);
        // Try localStorage fallback on error
        const stored = localStorage.getItem('admin_press_releases');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.length > 0) {
            const formattedData = parsed.filter(item =>
              item.status?.toLowerCase() === 'published'
            ).map(item => ({
              id: item.id,
              title: item.title,
              date: item.date,
              excerpt: item.excerpt,
              image: item.image || item.image_url || 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop'
            }));
            if (formattedData.length > 0) {
              setPressReleases(sortByDateDesc(formattedData));
            }
          }
        }
      }
    };

    fetchPressReleases();
  }, []);

  // Podcast data for future use
  // eslint-disable-next-line no-unused-vars
  const podcasts = [
    {
      title: 'Truth in the Age of AI: A Conversation with Partsol Leadership',
      guest: 'CEO Interview',
      duration: '45 min',
      transcriptUrl: 'https://partsol.com/podcasts/truth-in-ai-transcript'
    },
    {
      title: 'Building Cognitive AI: The Engineering Behind Absolute Truth',
      guest: 'Tech Deep Dive',
      duration: '38 min',
      transcriptUrl: 'https://partsol.com/podcasts/cognitive-ai-engineering-transcript'
    },
    {
      title: 'Healthcare Decision Intelligence: Real-World Applications',
      guest: 'Industry Focus',
      duration: '52 min',
      transcriptUrl: 'https://partsol.com/podcasts/healthcare-decision-intelligence-transcript'
    }
  ];

  return (
    <div className="news-page">
      <section className="news-hero">
        <div className="news-hero-content">
          <p className="news-hero-label">In The News</p>
          <h1 className="news-hero-title">Insights & Updates</h1>
          <p className="news-hero-subtitle">
            Stay informed with the latest news, research, and thought leadership from Partsol
          </p>
        </div>
      </section>

      <section className="press-releases-section">
        <div className="press-releases-container">
          <h2 className="section-title">Press Releases</h2>
          <div className="press-releases-grid">
            {pressReleases.map((release) => (
              <Link to={`/news/press-release/${release.id}`} key={release.id} className="press-release-card-new">
                <div className="press-release-image">
                  <img src={release.image} alt={release.title} />
                  <span className="press-release-badge">Press Release</span>
                </div>
                <div className="press-release-card-content">
                  <h3 className="press-release-card-title">{release.title}</h3>
                  <p className="press-release-card-excerpt">{release.excerpt}</p>
                  <div className="press-release-card-meta">
                    <span><Calendar size={14} /> {release.date}</span>
                    <span className="read-more">Read More →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="in-the-news-section">
        <div className="in-the-news-container">
          <h2 className="section-title">In the News</h2>
          <p className="section-subtitle">Media coverage and features about Partsol</p>
          
          {/* Featured Videos */}
          <div className="featured-news-video">
            <div className="featured-video-info">
              <div className="news-source-badge">Fox 13 Tampa Bay</div>
              <h3 className="featured-video-title">Making Cent$ with Walter Allen Part 2</h3>
              <p className="featured-video-date"><Calendar size={14} /> February 25, 2026</p>
            </div>
            <div className="featured-video-wrapper">
              <iframe
                className="featured-video-iframe"
                src="https://www.youtube.com/embed/fOQChhZv_fs"
                title="Making Cent$ with Walter Allen Part 2 - Fox 13 Tampa Bay"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="featured-news-video">
            <div className="featured-video-info">
              <div className="news-source-badge">Fox 13 Tampa Bay</div>
              <h3 className="featured-video-title">Making Cent$ with Walter Allen</h3>
              <p className="featured-video-date"><Calendar size={14} /> February 20, 2026</p>
            </div>
            <div className="featured-video-wrapper">
              <iframe
                className="featured-video-iframe"
                src="https://www.youtube.com/embed/_rgg6s6kTg0"
                title="Making Cent$ with Walter Allen - Fox 13 Tampa Bay"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="featured-news-video">
            <div className="featured-video-info">
              <div className="news-source-badge">Just The News</div>
              <h3 className="featured-video-title">AI is the Talk of the Tech World</h3>
              <p className="featured-video-date"><Calendar size={14} /> February 15, 2026</p>
            </div>
            <div className="featured-video-wrapper">
              <iframe
                className="featured-video-iframe"
                src="https://www.youtube.com/embed/efjarFIZIv0"
                title="AI is the Talk of the Tech World - Just The News"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="in-the-news-list">
            <a
              href="https://www.bizjournals.com/tampabay/news/2026/02/05/partsol-funding-update.html?utm_source=st&utm_medium=en&utm_campaign=inno&ana=e_n&utm_content=ta"
              target="_blank"
              rel="noopener noreferrer"
              className="in-the-news-card"
            >
              <div className="news-source-badge">Tampa Bay Business Journal</div>
              <h3 className="in-the-news-title">Tampa AI Company eyes $40M capital raise following launch of defense arm</h3>
              <div className="in-the-news-meta">
                <span><Calendar size={14} /> February 5, 2026</span>
                <span className="external-link-label"><ExternalLink size={14} /> Read Article</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="podcasts-section">
        <div className="podcasts-container">
          <h2 className="section-title">Podcasts</h2>
          <p className="section-subtitle">Listen to conversations about Cognitive AI, industry insights, and the future of decision intelligence</p>
          <div className="podcasts-coming-soon">
            <div className="coming-soon-icon">
              <Headphones size={48} />
            </div>
            <h3 className="coming-soon-title">Coming Soon</h3>
            <p className="coming-soon-text">
              We're working on exciting podcast content featuring conversations with industry leaders,
              deep dives into Cognitive AI technology, and insights on the future of decision intelligence.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;
